/**
 * FunnelManager — Funnels & Datasets function group, MVP slice.
 *
 * The original use case from Session 1: question-driven data collection.
 * A funnel is a sequence of "surfaces" (questions); a user opens it,
 * answers each in turn, and either completes (submits to the dataset)
 * or abandons. Drafts persist locally so a user can resume.
 *
 * Scope (function map MVP):
 *   - registerDefinition() — store a funnel definition by content hash
 *   - getDefinition()      — fetch a cached definition
 *   - openFunnel()         — start (or resume) a funnel run
 *   - resumeFunnel()       — load existing draft
 *   - submitAnswer()       — append an answer to an in-progress draft
 *   - abandonFunnel()      — delete a draft
 *   - completeFunnel()     — finalize draft → submissions log + audit
 *   - listOwnSubmissions() — read own_submissions cache
 *
 * Not in scope (beta):
 *   - exportOwnSubmissions (small wrapper over listOwnSubmissions; trivial)
 *   - proveSubmission (zero-knowledge proof; large undertaking)
 *   - viewDatasetAggregate (requires aggregator role + dataset stream)
 *   - subscribeToFunnel (live updates to definitions)
 *   - updateFunnelDefinition (signed definition publishing)
 *
 * Encryption note: drafts include user answers, which can be sensitive.
 * The `answersCiphertext` field on FunnelDraftRecord is AES-GCM encrypted
 * under the session key; in plaintext, the manager always operates on
 * a `Record<string, unknown>` of {surfaceId: answer}.
 *
 * Funnel definitions are stored unencrypted — they're not sensitive
 * (just question text + schema). Definitions are content-addressed by
 * SHA-256 hash so multiple versions coexist and submissions can reference
 * the exact definition they ran against.
 */

import type { AppContext } from '../app';
import { signEnvelope } from '../envelope/Envelope';
import type { Context } from '../identity';
import type {
  AuditLogRecord,
  FunnelDefinitionRecord,
  FunnelDraftRecord,
  OwnSubmissionRecord,
} from '../storage/schema';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

export interface RegisterDefinitionOptions {
  funnelId: string;
  version: string;
  /** Funnel definition object — JSON-serializable. Implementation-defined
   * shape; this manager treats it as opaque except for storage. */
  definition: Record<string, unknown>;
}

export interface RegisterDefinitionResult {
  /** Content hash of the stored definition (hex). */
  hash: string;
  funnelId: string;
  version: string;
}

export interface OpenFunnelOptions {
  funnelId: string;
  /** If multiple definition versions are cached, pick this one. Defaults
   * to the most recently fetched. */
  version?: string;
  context?: Context;
}

export interface OpenFunnelResult {
  funnelId: string;
  funnelHash: string;
  /** Decoded definition for the runner to consume. */
  definition: Record<string, unknown>;
  /** Existing answers if there's a resumable draft, else empty. */
  answers: Record<string, unknown>;
  /** Surface ID the user was last on. Null if not started or no
   * `currentSurfaceId` recorded. */
  currentSurfaceId: string | null;
}

export interface SubmitAnswerOptions {
  funnelId: string;
  surfaceId: string;
  answer: unknown;
  /** Optional: update the "current surface" marker (typically the next
   * surface in the sequence). The runner is responsible for computing
   * this; the manager just persists it. */
  nextSurfaceId?: string;
  context?: Context;
}

export interface CompleteFunnelOptions {
  funnelId: string;
  context?: Context;
}

export interface CompleteFunnelResult {
  submissionId: string;
  /** Hash of the published envelope. */
  envelopeHash: string;
  /** Final answer set submitted. */
  answers: Record<string, unknown>;
  /** Funnel definition hash used. */
  funnelHash: string;
}

export interface ListOwnSubmissionsOptions {
  context?: Context;
  funnelId?: string;
  /** Max rows. Default 100. */
  limit?: number;
}

export interface OwnSubmissionView {
  submissionId: string;
  funnelId: string;
  funnelHash: string;
  submittedAt: string;
  answers: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPLEMENTATION
// ─────────────────────────────────────────────────────────────────────────────

export class FunnelManager {
  constructor(private ctx: AppContext) {}

  // ───────────────────────────────────────────────────────────────────────
  // registerDefinition
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Store a funnel definition locally, keyed by content hash. Definitions
   * are immutable once registered — a new version replaces nothing but
   * adds another row.
   */
  async registerDefinition(opts: RegisterDefinitionOptions): Promise<RegisterDefinitionResult> {
    const bytes = new TextEncoder().encode(canonicalJSON(opts.definition));
    const hash = await hashHex(bytes);

    const record: FunnelDefinitionRecord = {
      hash,
      funnelId: opts.funnelId,
      version: opts.version,
      fetchedAt: new Date().toISOString(),
      definition: bytes,
    };
    await this.ctx.storage.db.funnel_definitions.put(record);

    await this.audit({
      category: 'funnel',
      action: 'funnel.definition-registered',
      context: null,
      details: { funnelId: opts.funnelId, version: opts.version, hash },
    });

    return { hash, funnelId: opts.funnelId, version: opts.version };
  }

  // ───────────────────────────────────────────────────────────────────────
  // getDefinition
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Fetch a definition by funnelId, optionally pinned to a specific
   * version. Returns the most-recently-fetched version when version
   * is unspecified.
   */
  async getDefinition(funnelId: string, version?: string): Promise<{
    hash: string;
    funnelId: string;
    version: string;
    definition: Record<string, unknown>;
    fetchedAt: string;
  } | undefined> {
    const db = this.ctx.storage.db;

    let row: FunnelDefinitionRecord | undefined;
    if (version !== undefined) {
      row = await db.funnel_definitions
        .where('[funnelId+version]')
        .equals([funnelId, version])
        .first();
    } else {
      const all = await db.funnel_definitions
        .where('funnelId').equals(funnelId).toArray();
      // Most-recently-fetched first.
      all.sort((a, b) => b.fetchedAt.localeCompare(a.fetchedAt));
      row = all[0];
    }
    if (row === undefined) return undefined;

    const definition = JSON.parse(new TextDecoder().decode(row.definition)) as Record<string, unknown>;
    return {
      hash: row.hash,
      funnelId: row.funnelId,
      version: row.version,
      definition,
      fetchedAt: row.fetchedAt,
    };
  }

  // ───────────────────────────────────────────────────────────────────────
  // openFunnel
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Open a funnel. If a draft exists, return it; otherwise return a fresh
   * empty answer set. Either way, returns the decoded definition for the
   * runner to render.
   *
   * Throws if no definition is cached for the requested funnelId.
   */
  async openFunnel(opts: OpenFunnelOptions): Promise<OpenFunnelResult> {
    const definitionInfo = await this.getDefinition(opts.funnelId, opts.version);
    if (definitionInfo === undefined) {
      throw new Error(
        `FunnelManager.openFunnel: no cached definition for ${opts.funnelId}` +
          (opts.version !== undefined ? `@${opts.version}` : ''),
      );
    }

    const context = opts.context ?? defaultContext();
    const draft = await this.loadDraft(opts.funnelId, context);

    if (draft !== undefined) {
      return {
        funnelId: opts.funnelId,
        funnelHash: definitionInfo.hash,
        definition: definitionInfo.definition,
        answers: draft.answers,
        currentSurfaceId: draft.currentSurfaceId,
      };
    }

    // No draft — return fresh empty state.
    return {
      funnelId: opts.funnelId,
      funnelHash: definitionInfo.hash,
      definition: definitionInfo.definition,
      answers: {},
      currentSurfaceId: null,
    };
  }

  // ───────────────────────────────────────────────────────────────────────
  // resumeFunnel
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Load only the in-progress draft (no definition fetch). Returns
   * undefined if no draft exists. Useful when the caller already has the
   * definition cached client-side.
   */
  async resumeFunnel(funnelId: string, context?: Context): Promise<{
    funnelId: string;
    funnelHash: string;
    answers: Record<string, unknown>;
    currentSurfaceId: string | null;
    startedAt: string;
    updatedAt: string;
  } | undefined> {
    const ctxKey = contextToKey(context ?? defaultContext());
    const id = draftId(ctxKey, funnelId);
    const row = await this.ctx.storage.db.funnel_drafts.get(id);
    if (row === undefined) return undefined;

    const answersBytes = await this.ctx.storage.decryptForSession(row.answersCiphertext);
    const answers = JSON.parse(new TextDecoder().decode(answersBytes)) as Record<string, unknown>;

    return {
      funnelId: row.funnelId,
      funnelHash: row.funnelHash,
      answers,
      currentSurfaceId: row.currentSurfaceId === '' ? null : row.currentSurfaceId,
      startedAt: row.startedAt,
      updatedAt: row.updatedAt,
    };
  }

  // ───────────────────────────────────────────────────────────────────────
  // submitAnswer
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Append an answer to an in-progress draft (creating the draft if needed).
   * The answer can be any JSON-serializable value; this manager doesn't
   * validate against the definition's schema. (Validation is the runner's
   * job — and the runner can fetch the definition via getDefinition.)
   */
  async submitAnswer(opts: SubmitAnswerOptions): Promise<void> {
    const ctx = this.ctx;
    const context = opts.context ?? defaultContext();
    const ctxKey = contextToKey(context);
    const id = draftId(ctxKey, opts.funnelId);
    const now = new Date().toISOString();

    // Get definition hash to bind the draft to a specific version.
    const def = await this.getDefinition(opts.funnelId);
    if (def === undefined) {
      throw new Error(
        `FunnelManager.submitAnswer: no cached definition for ${opts.funnelId}; ` +
          'call registerDefinition first.',
      );
    }

    // Load or create draft.
    const existing = await ctx.storage.db.funnel_drafts.get(id);
    let answers: Record<string, unknown>;
    let startedAt: string;
    if (existing === undefined) {
      answers = {};
      startedAt = now;
    } else {
      const bytes = await ctx.storage.decryptForSession(existing.answersCiphertext);
      answers = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
      startedAt = existing.startedAt;
    }

    answers[opts.surfaceId] = opts.answer;

    const answersCiphertext = await ctx.storage.encryptForSession(
      new TextEncoder().encode(JSON.stringify(answers)),
    );

    const record: FunnelDraftRecord = {
      id,
      context: ctxKey,
      funnelId: opts.funnelId,
      funnelHash: def.hash,
      currentSurfaceId: opts.nextSurfaceId ?? opts.surfaceId,
      answersCiphertext,
      startedAt,
      updatedAt: now,
    };

    await ctx.storage.db.funnel_drafts.put(record);
    // submitAnswer is high-volume; skip audit to avoid log bloat. The
    // completion entry covers the eventual submission.
  }

  // ───────────────────────────────────────────────────────────────────────
  // abandonFunnel
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Discard an in-progress draft. Idempotent — no error if no draft exists.
   */
  async abandonFunnel(funnelId: string, context?: Context): Promise<void> {
    const ctx = this.ctx;
    const ctxKey = contextToKey(context ?? defaultContext());
    const id = draftId(ctxKey, funnelId);
    const existing = await ctx.storage.db.funnel_drafts.get(id);
    if (existing === undefined) return;
    await ctx.storage.db.funnel_drafts.delete(id);

    await this.audit({
      category: 'funnel',
      action: 'funnel.abandoned',
      context: ctxKey,
      details: { funnelId, answeredCount: countKeys(existing) },
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // completeFunnel
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Finalize an in-progress draft. Steps:
   *   1. Load the draft.
   *   2. Build a FunnelSubmissionPayload envelope, signed under the funnel
   *      context's per-context sub-key.
   *   3. Open the per-funnel submissions stream (mesh-submissions-{funnelId})
   *      and append the envelope.
   *   4. Insert into own_submissions for fast local lookback.
   *   5. Delete the draft.
   *   6. Audit.
   */
  async completeFunnel(opts: CompleteFunnelOptions): Promise<CompleteFunnelResult> {
    const ctx = this.ctx;
    const context = opts.context ?? defaultContext();
    const ctxKey = contextToKey(context);
    const id = draftId(ctxKey, opts.funnelId);
    const db = ctx.storage.db;
    const now = new Date().toISOString();

    const draft = await db.funnel_drafts.get(id);
    if (draft === undefined) {
      throw new Error(`FunnelManager.completeFunnel: no draft for ${opts.funnelId}`);
    }

    const answersBytes = await ctx.storage.decryptForSession(draft.answersCiphertext);
    const answers = JSON.parse(new TextDecoder().decode(answersBytes)) as Record<string, unknown>;

    // Build the envelope payload.
    const payload = {
      kind: 'funnel-submission' as const,
      funnelId: opts.funnelId,
      funnelHash: draft.funnelHash,
      answers,
      nonce: randomHex(16),
    };
    const envelopePayloadStr = JSON.stringify(payload);

    // Open the submissions stream if not already.
    const streamId = `mesh-submissions-${opts.funnelId}`;
    try {
      await ctx.sync.open(streamId, context);
    } catch (err) {
      // Already open is fine.
      if (!/already/i.test(errMsg(err))) throw err;
    }

    const env = await signEnvelope({
      identity: ctx.identity,
      context,
      contentType: 'application/json; profile=funnel-submission',
      payload: envelopePayloadStr,
      lamport: 0,
      parentHash: '',
    });

    await ctx.sync.append(streamId, env);

    // Cache locally.
    const ownRecord: OwnSubmissionRecord = {
      submissionId: env.hash,
      context: ctxKey,
      funnelId: opts.funnelId,
      funnelHash: draft.funnelHash,
      submittedAt: now,
      ciphertext: await ctx.storage.encryptForSession(
        new TextEncoder().encode(envelopePayloadStr),
      ),
    };

    await db.transaction('rw', [db.own_submissions, db.funnel_drafts], async () => {
      await db.own_submissions.put(ownRecord);
      await db.funnel_drafts.delete(id);
    });

    await this.audit({
      category: 'funnel',
      action: 'funnel.completed',
      context: ctxKey,
      details: {
        funnelId: opts.funnelId,
        funnelHash: draft.funnelHash,
        envelopeHash: env.hash,
        answeredCount: Object.keys(answers).length,
      },
    });

    return {
      submissionId: env.hash,
      envelopeHash: env.hash,
      answers,
      funnelHash: draft.funnelHash,
    };
  }

  // ───────────────────────────────────────────────────────────────────────
  // listOwnSubmissions
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Read the local cache of submissions this device has made. Returns
   * decrypted, deserialized records sorted by submittedAt descending.
   *
   * Filter by funnelId or context. Default returns everything (across
   * all contexts), most-recent first.
   */
  async listOwnSubmissions(opts: ListOwnSubmissionsOptions = {}): Promise<OwnSubmissionView[]> {
    const ctx = this.ctx;
    const db = ctx.storage.db;
    const limit = opts.limit ?? 100;

    let rows: OwnSubmissionRecord[];
    if (opts.context !== undefined && opts.funnelId !== undefined) {
      const ctxKey = contextToKey(opts.context);
      rows = await db.own_submissions
        .where('[context+funnelId]')
        .equals([ctxKey, opts.funnelId])
        .toArray();
    } else if (opts.funnelId !== undefined) {
      rows = await db.own_submissions.toArray();
      rows = rows.filter((r) => r.funnelId === opts.funnelId);
    } else if (opts.context !== undefined) {
      const ctxKey = contextToKey(opts.context);
      rows = await db.own_submissions.where('context').equals(ctxKey).toArray();
    } else {
      rows = await db.own_submissions.toArray();
    }

    rows.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
    rows = rows.slice(0, limit);

    const out: OwnSubmissionView[] = [];
    for (const r of rows) {
      const bytes = await ctx.storage.decryptForSession(r.ciphertext);
      const parsed = JSON.parse(new TextDecoder().decode(bytes)) as { answers?: unknown };
      const answers = (parsed.answers ?? {}) as Record<string, unknown>;
      out.push({
        submissionId: r.submissionId,
        funnelId: r.funnelId,
        funnelHash: r.funnelHash,
        submittedAt: r.submittedAt,
        answers,
      });
    }
    return out;
  }

  // ───────────────────────────────────────────────────────────────────────
  // INTERNAL
  // ───────────────────────────────────────────────────────────────────────

  private async loadDraft(
    funnelId: string,
    context: Context,
  ): Promise<{
    funnelHash: string;
    currentSurfaceId: string | null;
    answers: Record<string, unknown>;
    startedAt: string;
    updatedAt: string;
  } | undefined> {
    const ctxKey = contextToKey(context);
    const id = draftId(ctxKey, funnelId);
    const row = await this.ctx.storage.db.funnel_drafts.get(id);
    if (row === undefined) return undefined;

    const bytes = await this.ctx.storage.decryptForSession(row.answersCiphertext);
    const answers = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    return {
      funnelHash: row.funnelHash,
      currentSurfaceId: row.currentSurfaceId === '' ? null : row.currentSurfaceId,
      answers,
      startedAt: row.startedAt,
      updatedAt: row.updatedAt,
    };
  }

  private async audit(input: {
    category: AuditLogRecord['category'];
    action: string;
    context: string | null;
    details: Record<string, unknown>;
  }): Promise<void> {
    const ctx = this.ctx;
    const occurredAt = new Date().toISOString();
    const canonical = JSON.stringify({
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    const signature = await ctx.identity.sign(
      new TextEncoder().encode(canonical),
      defaultContext(),
    );
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: input.category,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };
    try {
      await ctx.storage.db.audit_log.add(record);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`FunnelManager.audit: failed to write audit log: ${errMsg(err)}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function defaultContext(): Context {
  return { environment: 'funnel', purpose: 'funnel-default' };
}

function contextToKey(ctx: Context): string {
  return `${ctx.environment}:${ctx.purpose}${ctx.epoch !== undefined ? `:e${ctx.epoch}` : ''}`;
}

function draftId(contextKey: string, funnelId: string): string {
  return `${contextKey}:${funnelId}`;
}

function canonicalJSON(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map((v) => canonicalJSON(v)).join(',') + ']';
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalJSON(obj[k])).join(',') + '}';
}

async function hashHex(b: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', b as BufferSource);
  const arr = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < arr.length; i++) s += arr[i]!.toString(16).padStart(2, '0');
  return s;
}

function randomHex(byteLen: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLen));
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i]!.toString(16).padStart(2, '0');
  return s;
}

function countKeys(draft: FunnelDraftRecord): number {
  // Without decrypting, we can't know the exact count. The draft record
  // is encrypted under the session key. The caller is expected to be the
  // owning user; if they want the exact count they can decrypt. For audit
  // purposes "0 or more" is enough.
  void draft;
  return 0;
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
